import { useQuery } from '@tanstack/react-query'
import { Flex, Skeleton, Spin, Table, Tag, Input, Button, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import $api from '../../API'
import { useCallback, useEffect, useRef, useState } from 'react'
import { parseISO } from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { TableDateFilter } from '../../components/table-filters/date'

type Duration = {
  hours?: number
  minutes?: number
}

type ExecutionInfoStalling = {
  id: string
  type: string
  startTime: string
  runTime: Duration
}

type ExecutionInfo = {
  id: string
  runId: string
  type: string
  status: string
  startTime: string
  endTime: string
}

type WorkflowResult = {
  status: string
  result: unknown
}

type Summary = {
  running: number
  completed: number
  failed: number
}

function renderDate(date: string) {
  return <span>{parseISO(date).toLocaleString()}</span>
}

function renderDuration(duration: Duration) {
  const { hours, minutes } = duration

  if (!hours && !minutes) {
    return '0'
  }

  if (!hours) {
    return `${minutes} minutes`
  }

  if (!minutes) {
    return `${hours} hours`
  }

  return `${hours} hours, ${minutes} minutes`
}

function generateKey(row: { id: string; runId?: string }) {
  return `${row.id}-${row.runId ?? 'no-run-id'}`
}

export function TemporalDiagnostic(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  const [summary, setSummary] = useState<Summary>({ running: 0, completed: 0, failed: 0 })
  const [stalling, setStalling] = useState<ExecutionInfoStalling[]>([])
  const [failed, setFailed] = useState<ExecutionInfo[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const pageSizeRef = useRef(pageSize)

  const [failureResults, setFailureResults] = useState(new Map<string, WorkflowResult>())

  const summaryQuery = useQuery({
    queryKey: ['temporal-summary'],
    queryFn: async () => {
      const response = await $api.get<Summary>('/diagnostic/temporal/summary')
      return response.data
    }
  })

  const stallingQuery = useQuery({
    queryKey: ['temporal-stalled'],
    queryFn: async () => {
      const response = await $api.get<ExecutionInfoStalling[]>('/diagnostic/temporal/list/stalling')
      return response.data
    }
  })

  const failedQuery = useQuery({
    queryKey: ['temporal-failed', searchParams.toString()],
    queryFn: async () => {
      const params = new URLSearchParams(searchParams)
      params.set('status', 'Failed')

      const response = await $api.get<ExecutionInfo[]>('/diagnostic/temporal/list', { params })
      return response.data
    }
  })

  useEffect(() => {
    if (summaryQuery.data !== undefined) {
      setSummary(summaryQuery.data)
    }
  }, [summaryQuery.data, setSummary])

  useEffect(() => {
    if (stallingQuery.data !== undefined) {
      setStalling(stallingQuery.data)
    }
  }, [stallingQuery.data, setStalling])

  useEffect(() => {
    if (failedQuery.data !== undefined) {
      setFailed(failedQuery.data)
    }
  }, [failedQuery.data, setFailed])

  const resetPagination = useCallback(() => {
    setCurrentPage(1)
  }, [])

  const handleFailedExpand = async (expanded: boolean, row: ExecutionInfo) => {
    if (!expanded) {
      return
    }

    const params = new URLSearchParams({ workflow: row.id, run: row.runId })
    const response = await $api.get<WorkflowResult>(`/diagnostic/temporal/result?${params}`)

    setFailureResults(prev => {
      const newMap = new Map(prev)
      newMap.set(generateKey(row), response.data)
      return newMap
    })
  }

  const getColumnSearchProps = (dataIndex: string, label: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
        <Input
          placeholder={`Search ${label}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.()
              confirm()
            }}
            size='small'
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    filteredValue: searchParams.get(dataIndex === 'id' ? 'workflowId' : 'workflowType')
      ? [(searchParams.get(dataIndex === 'id' ? 'workflowId' : 'workflowType') ?? '') as string]
      : null
  })

  const failureReasonElement = (row: ExecutionInfo) => {
    const rowKey = generateKey(row)
    return failureResults.has(rowKey) ? (
      <pre className='text-xs bg-gray-100 border p-2 rounded'>
        {JSON.stringify(failureResults.get(rowKey)?.result ?? 'n/a', undefined, 4)}
      </pre>
    ) : (
      <Flex justify='center' align='center'>
        <Spin />
      </Flex>
    )
  }

  return (
    <div className='flex flex-col gap-4 p-6 font-normal font-lato rounded-lg bg-lucent-background text-lucent-text'>
      <h1 className='text-2xl'>Temporal Overview</h1>

      <Skeleton active loading={summaryQuery.isLoading}>
        <Flex vertical gap='middle' className='pl-4'>
          <h2 className='text-xl'>Summary</h2>
          <Flex gap='small' align='left'>
            <Tag color='geekblue'>{summary.running} running</Tag>
            <Tag color='green'>{summary.completed} completed</Tag>
            <Tag color='red'>{summary.failed} failed</Tag>
          </Flex>
        </Flex>
      </Skeleton>

      <Table<ExecutionInfoStalling>
        size={'small'}
        rowKey={generateKey}
        columns={[
          {
            title: 'Type',
            dataIndex: 'type'
          },
          {
            title: 'Workflow ID',
            dataIndex: 'id'
          },
          {
            title: 'Start Time',
            dataIndex: 'startTime',
            sorter: (a, b) => a.startTime.localeCompare(b.startTime),
            render: renderDate
          },
          {
            title: 'Run Time',
            dataIndex: 'runTime',
            defaultSortOrder: 'descend',
            sorter: (a, b) => {
              const h = (a.runTime.hours ?? 0) - (b.runTime.hours ?? 0)
              return h === 0 ? (a.runTime.minutes ?? 0) - (b.runTime.minutes ?? 0) : h
            },
            render: x => <Tag color={x.hours >= 24 ? 'red' : 'orange'}>{renderDuration(x)}</Tag>
          }
        ]}
        dataSource={stalling}
        bordered
        showSorterTooltip={{ target: 'sorter-icon' }}
        title={() => <h2 className='text-xl'>{'Workflows running for more than 6 hours'}</h2>}
        loading={stallingQuery.isLoading}
      />

      <Table<ExecutionInfo>
        size={'small'}
        rowKey={generateKey}
        columns={[
          {
            title: 'Type',
            dataIndex: 'type',
            ...getColumnSearchProps('type', 'Workflow Type')
          },
          {
            title: 'Workflow ID',
            dataIndex: 'id',
            ...getColumnSearchProps('id', 'Workflow ID')
          },
          {
            title: 'Start Time',
            dataIndex: 'startTime',
            sorter: (a, b) => a.startTime.localeCompare(b.startTime),
            render: renderDate,
            filterDropdown: (props: FilterDropdownProps) => <TableDateFilter {...props} showTime />,
            filteredValue:
              (typeof searchParams.get('from') === 'string' &&
                searchParams.get('from')!.length > 0) ||
              (typeof searchParams.get('to') === 'string' && searchParams.get('to')!.length > 0)
                ? [searchParams.get('from') ?? '', searchParams.get('to') ?? '']
                : []
          },
          {
            title: 'End Time',
            dataIndex: 'endTime',
            sorter: (a, b) => a.endTime.localeCompare(b.endTime),
            render: (val: string) => (val === 'n/a' ? val : renderDate(val)),
            filterDropdown: (props: FilterDropdownProps) => <TableDateFilter {...props} showTime />,
            filteredValue:
              (typeof searchParams.get('endFrom') === 'string' &&
                searchParams.get('endFrom')!.length > 0) ||
              (typeof searchParams.get('endTo') === 'string' &&
                searchParams.get('endTo')!.length > 0)
                ? [searchParams.get('endFrom') ?? '', searchParams.get('endTo') ?? '']
                : []
          }
        ]}
        dataSource={failed}
        bordered
        showSorterTooltip={{ target: 'sorter-icon' }}
        title={() => <h2 className='text-xl'>Failed workflows</h2>}
        expandable={{
          onExpand: handleFailedExpand,
          expandedRowRender: failureReasonElement,
          rowExpandable: _row => true
        }}
        loading={failedQuery.isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          pageSizeOptions: ['10', '25', '50', '100'],
          showSizeChanger: true,
          total: failed.length,
          showTotal(total, [from, to]) {
            return (
              <span className='text-sm text-gray-500 font-lato'>
                Showing {from}–{to} of {total} {total === 1 ? 'item' : 'items'}
              </span>
            )
          },
          hideOnSinglePage: false
        }}
        onChange={(pagination, filters, _sorters, extra) => {
          if (extra.action === 'filter') {
            const newParams = new URLSearchParams(searchParams)

            if (filters.type?.[0]) {
              newParams.set('workflowType', filters.type[0] as string)
            } else {
              newParams.delete('workflowType')
            }

            if (filters.id?.[0]) {
              newParams.set('workflowId', filters.id[0] as string)
            } else {
              newParams.delete('workflowId')
            }

            const [sf, st] = (filters.startTime as string[] | null) ?? []
            if (sf) newParams.set('from', dayjs(sf).toISOString())
            else newParams.delete('from')
            if (st) newParams.set('to', dayjs(st).toISOString())
            else newParams.delete('to')

            const [ef, et] = (filters.endTime as string[] | null) ?? []
            if (ef) newParams.set('endFrom', dayjs(ef).toISOString())
            else newParams.delete('endFrom')
            if (et) newParams.set('endTo', dayjs(et).toISOString())
            else newParams.delete('endTo')

            setSearchParams(newParams)
            resetPagination()
          }

          if (extra.action === 'paginate') {
            setCurrentPage(pagination.current ?? 1)

            if ((pagination.pageSize ?? 10) !== pageSizeRef.current) {
              setPageSize(pagination.pageSize ?? 10)
              pageSizeRef.current = pagination.pageSize ?? 10
              setCurrentPage(1)
            }
          }
        }}
      />
    </div>
  )
}
